import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App.jsx";
import "./index.css";
import { isModuleLoadError } from "./utils/moduleLoadError";

console.log("=== MAIN.JSX STARTING ===");

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (isModuleLoadError(this.state.error)) {
        return (
          <main style={{ padding: "24px", fontFamily: "Arial", background: "#fff", color: "#12324d", minHeight: "100vh" }}>
            <h1>Reload the latest app</h1>
            <p>A page could not load. An app update or a connection problem may have made its files unavailable.</p>
            <p>Reconnect if needed, then reload. Unsaved entries on this screen may be lost; this does not submit or retry a payment or refund.</p>
            <button type="button" onClick={() => window.location.reload()}>Reload latest app</button>
          </main>
        );
      }
      return (
        <div style={{ padding: "20px", color: "red", fontFamily: "Arial" }}>
          <h1>Error Loading App</h1>
          <p>{this.state.error?.message}</p>
          <pre style={{ background: "#f0f0f0", padding: "10px", overflow: "auto" }}>
            {this.state.error?.stack}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}

const root = ReactDOM.createRoot(document.getElementById("root"));
console.log("Root created");

try {
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>
  );
  console.log("App rendered with error boundary");
} catch (error) {
  console.error("Failed to render:", error);
}

// Keep the worker out of Vite development sessions; register it for deployed PWA builds.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') return;

    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      let reloadRequested = false;

      function showUpdateBanner() {
        if (document.getElementById('update-banner')) return;
        const bar = document.createElement('div');
        bar.id = 'update-banner';
        bar.style.cssText = 'position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:9999;background:#111827;color:#fff;padding:10px 14px;border-radius:10px;box-shadow:0 10px 20px rgba(0,0,0,.2);display:flex;gap:10px;align-items:center;';
        bar.innerHTML = '<span>Update available</span>';
        const btn = document.createElement('button');
        btn.textContent = 'Reload';
        btn.style.cssText = 'background:#10b981;border:0;color:#fff;padding:6px 10px;border-radius:8px;cursor:pointer;font-weight:600;';
        btn.onclick = () => {
          if (reg.waiting) {
            reloadRequested = true;
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          } else {
            window.location.reload();
          }
        };
        bar.appendChild(btn);
        document.body.appendChild(bar);
      }

      // Detect waiting SW (already installed)
      if (reg.waiting) showUpdateBanner();

      // Listen for updates
      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            showUpdateBanner();
          }
        });
      });

      // Reload once controller changes (after SKIP_WAITING)
      // A worker can activate while forms are open. Let the user choose when to reload.
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloadRequested) window.location.reload();
        else showUpdateBanner();
      });
    } catch (e) {
      console.warn('Progressive web app worker could not be registered:', e);
    }
  });
}
